import React, { useState, useEffect } from 'react';
import { withFirebase } from '../../Firebase';
import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Alert
} from 'reactstrap';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// Utility to extract model name from Gemini API endpoint
function getGeminiModelName() {
  // Keep in sync with src/services/gemini.service.ts and src/services/keywordExtractor.ts
  const API_BASE_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent';
  const match = API_BASE_URL.match(/models\/(.+?):/);
  return match ? match[1] : 'Unknown';
}

const ChatbotAdmin = ({ firebase }) => {
  const [config, setConfig] = useState({
    apiKey: '',
    enabled: false,
  });
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const functions = getFunctions(getApp(), 'europe-west3');

  useEffect(() => {
    // Define and call loadConfig directly inside useEffect
    async function loadConfig() {
      setLoading(true);
      try {
        const configDocRef = doc(firebase.db, 'chatbotConfig', 'gemini');
        const docSnap = await getDoc(configDocRef);
        if (docSnap.exists()) {
          const configData = docSnap.data();
          setConfig(configData);
          setOriginalConfig(configData);
        } else {
          // Create default config if it doesn't exist
          const defaultConfig = {
            apiKey: '',
            enabled: false,
            lastUpdated: firebase.db.FieldValue.serverTimestamp()
          };
          await setDoc(configDocRef, defaultConfig);
          setConfig(defaultConfig);
          setOriginalConfig(defaultConfig);
        }
      } catch (err) {
        setError('Error loading configuration: ' + err.message);
      } finally {
        setLoading(false);
      }
    }

    loadConfig();
  }, [firebase]); // Add firebase as a dependency

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig({
      ...config,
      [name]: (type === 'checkbox' || type === 'switch') ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const configDocRef = doc(firebase.db, 'chatbotConfig', 'gemini');
      await updateDoc(configDocRef, {
        ...config,
        lastUpdated: new Date()
      });
      setOriginalConfig(config);
      setSuccess(true);
    } catch (err) {
      setError('Error saving configuration: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInitialize = async () => {
    // Add confirmation dialog
    if (!window.confirm('Warning: This will initialize or reset the chatbot collections. Any existing data in these collections will be overwritten. Are you sure you want to continue?')) {
      return;
    }

    setError(null);

    try {
      // Debug: Log current user and token
      const user = firebase.auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        // Decode the token to check aud and user_id
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        // eslint-disable-next-line no-unused-vars
        const decoded = JSON.parse(jsonPayload);
      } else {
      }
      const initChatbotCollections = httpsCallable(functions, 'initChatbotCollections');
      const result = await initChatbotCollections();
      if (result.data && result.data.success) {
        setSuccess('Chatbot collections initialized successfully');
        // Reload configurations after successful initialization
        const configDocRef = doc(firebase.db, 'chatbotConfig', 'gemini');
        const docSnap = await getDoc(configDocRef);
        if (docSnap.exists()) {
          const configData = docSnap.data();
          setConfig(configData);
          setOriginalConfig(configData);
        }
      } else {
        setError('Failed to initialize collections');
      }
    } catch (error) {
      console.error('Error initializing collections:', error);
      setError(error.message || 'An error occurred while initializing collections');
    }
  };

  const handleTestConnection = async () => {
    if (!config.apiKey) {
      setError('API key is required to test connection');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const testGeminiConnection = httpsCallable(functions, 'testGeminiConnection');
      const result = await testGeminiConnection();
      if (result.data && result.data.success) {
        setSuccess(result.data.message);
      } else {
        setError('Connection test failed: Unexpected response format');
      }
    } catch (err) {
      setError('Connection test failed: ' + (err && err.message ? err.message : 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <Card className="no-bg-card">
        <CardBody className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Loading configuration...</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="no-bg-card">
      <CardHeader>
        <h2 className="mb-0">Chatbot Configuration</h2>
      </CardHeader>
      <CardBody>
        <div className="mb-3" style={{ color: '#ffffff' }}>
          <strong>Current LLM Model:</strong> {getGeminiModelName()}
        </div>
        {error && (
          <Alert color="danger" className="mb-4">
            {error}
          </Alert>
        )}
        {success && (
          <Alert color="success" className="mb-4">
            {typeof success === 'string' ? success : 'Configuration saved successfully!'}
          </Alert>
        )}

        <Button 
          className="mb-4 btn-info-custom btn-round" 
          onClick={handleInitialize}
          disabled={saving}
        >
          <span>Initialize Chatbot Collections</span>
        </Button>

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="apiKey">Gemini API Key</Label>
            <Input
              type="text"
              name="apiKey"
              id="apiKey"
              value={config.apiKey}
              onChange={handleChange}
              placeholder="Enter your Gemini API key"
              disabled={saving}
              style={{ maxWidth: '400px' }}
            />
            <small className="form-text text-muted">
              You can get a Gemini API key from the Google AI Studio.
            </small>
          </FormGroup>

          <FormGroup>
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: 0 }}>
              <Checkbox
                checked={!!config.enabled}
                onChange={e => handleChange({
                  target: {
                    name: 'enabled',
                    type: 'checkbox',
                    checked: e.target.checked
                  }
                })}
                icon={<CheckBoxOutlineBlankIcon style={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />}
                checkedIcon={<CheckBoxIcon style={{ color: '#fff', fontSize: 20 }} />}
                style={{ marginRight: 8, padding: 0 }}
                inputProps={{ id: 'enableChatbot' }}
              />
              <Label htmlFor="enableChatbot" style={{ margin: 0, fontWeight: 400, color: '#fff', cursor: 'pointer' }}>
                Enable Chatbot
              </Label>
            </div>
            <small className="form-text text-muted">
              When disabled, users won't be able to use the chatbot feature.
            </small>
          </FormGroup>

          <div className="d-flex">
            <Button
              type="submit"
              disabled={saving || JSON.stringify(config) === JSON.stringify(originalConfig)}
              className="mr-2 btn-danger-custom btn-round"
            >
              {saving ? (
                <span>
                  <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                  Saving...
                </span>
              ) : <span>Save Configuration</span>}
            </Button>
            <Button
              type="button"
              onClick={handleTestConnection}
              disabled={saving || !config.apiKey}
              className="btn-primary-custom btn-round"
            >
              <span>Test Connection</span>
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default withFirebase(ChatbotAdmin);